/**
 * THE SEASON, PLAYED ONE RACE AT A TIME — the screen over `seasonPlay.ts`.
 *
 * Howard, 17 Aug 2026:
 *
 *   "youve made it so you cant run a season/career week by week… If Season is
 *    checked on the race week you ought to be able to start the 1st race of
 *    your career right there too. Then race 2 and so on and so on
 *    one-at-a-time."
 *
 * That is what this panel is. It is HOME whenever a season is live (D-EZ-103:
 * the season home — play the next race, or simulate the rest of the year), and
 * it is a loop of exactly four states:
 *
 *     ready  →  the calendar's race N, and one button
 *     week   →  RWEG/p.2-4 revealed beat by beat, as an exhibition reveals it
 *     grid   →  step six, the 43 who start, and the green flag
 *     (race) →  the shell takes over: the tape, the boards, the booth
 *
 * and then back to `ready` on race N+1, thirty-six times, until `finishSeason`.
 *
 * ===========================================================================
 * WHERE THE RACE COMES FROM, AND WHY THE WEEK IS SHOWN BEFORE THE GREEN
 * ===========================================================================
 *
 * `runSeasonRace(state)` resolves the WEEK and the RACE in one call and hands
 * back both. It is pure: same state in, same week and same race out, every
 * time. So pressing "Race week" here calls it once and holds the step; the
 * reveal, the grid and then the tape are three views of a result that already
 * exists — which is precisely how this project has always shown a race, and the
 * reason "advance a turn" never means "roll again".
 *
 * NOTHING IS COMMITTED UNTIL THE GREEN. The season state advances only when the
 * player takes the green (`onGreen`), so backing out of a reveal costs nothing
 * and cannot skip a race. Coming back and pressing the button again re-runs the
 * identical race off the identical state — that is what purity buys.
 *
 * THE ONE THING WORTH READING TWICE — HAND-ROLLED SEASON RACES. When the player
 * ticks "roll my own dice" the race above is thrown away and the shell starts a
 * `ManualSeasonRace` on the SAME state. The WEEK on screen still stands: a race
 * week is rolled on `weekSeed`, before the first race die is drawn, and it does
 * not read `RaceOptions.dice` at all — so the hand-rolled race runs the week
 * that was just revealed, face for face. That is why the reveal is honest here
 * rather than a preview of something else.
 *
 * NOTHING IN THIS FILE IS A RULE. It calls the engine and draws what came back.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  isSeasonComplete,
  nextScheduledRace,
  runSeasonRace,
  seasonProgress,
  type SeasonRaceStep,
  type SeasonState,
} from '../domain/seasonPlay.ts';
import { REGULAR_SEASON_RACES, SEASON_RACES } from '../domain/schedule.ts';
import type { Team } from '../domain/field.ts';
import type { DriverCard } from '../domain/types.ts';
import type { CareerSeason } from '../lib/careerRunner.ts';
import { liveryForDriver, type Livery } from '../domain/livery.ts';
import { weekStory } from './raceWeekModel.ts';
import { feudHistories } from '../domain/feudHistory.ts';
import WeekReveal from './WeekReveal.tsx';
import RaceWeekStorylines from './story/Storylines.tsx';
import SeasonChapterCard from './chapters/SeasonChapterCard.tsx';
import StartingGrid from './StartingGrid.tsx';
import DriverDossier from './DriverDossier.tsx';
import RaceLogo from './RaceLogo.tsx';
import { ordinal } from './boardModel.ts';
import { Cite, ErrorNote, type FriendlyError } from './Cite.tsx';
import { friendlyError } from '../lib/prefs.ts';
// D-EZ-109 — the weekly Wire between races, and the paper it links to.
import { SeasonWire } from './Wire.tsx';
import { usePaperReader } from './story/Newsstand.tsx';

/** How long one beat of the race-week reveal holds before the next lands. */
const BEAT_MS = 620;

type Stage = 'ready' | 'week' | 'grid';

export interface SeasonPlayProps {
  /** The live season. Owned by `App.tsx` so it survives leaving this screen. */
  state: SeasonState;
  leagueName: string;
  seasonNumber: number;
  /**
   * The garage, for names and liveries only. The season's own `state.teams`
   * carries ids and rosters; this carries the paint.
   */
  teams: readonly Team[];
  /** The green flag: commit this race and take the player to the tape. */
  onGreen: (step: SeasonRaceStep) => void;
  /** D-018 — the same weekend, but the player rolls it. The shell owns the table. */
  onHandRoll: () => void;
  /**
   * A weekend the player has just hand-rolled, handed back by the dice table.
   *
   * ⚠ THIS EXISTS BECAUSE THE CHOICE MOVED, and the reason is worth keeping.
   * The hand-rolled feed used to cover the RACE only, so the week was identical
   * either way and it was safe to show the week first and offer the dice at the
   * grid. Now that the WEEK is hand-rolled too (Howard, 17 Aug 2026), offering
   * it there would have meant showing a player his grid, his TV list and his
   * feuds — and then replacing all three the moment he chose his own dice.
   * A screen that shows you a starting grid you are not going to race is the
   * kind of quiet lie this project does not ship.
   *
   * So the choice is made at 'ready', before a die is thrown, and the finished
   * weekend comes back HERE to be told exactly like a seeded one: the week's
   * story, then the grid, then the green.
   */
  handRolledStep?: SeasonRaceStep | null;
  /**
   * D-EZ-67 — THE SEASONS BEHIND THIS ONE, for the driver card and nothing else.
   *
   * ⚠ THE MODAL ONLY KNOWS WHAT THE SCREEN THAT OPENED IT KNOWS. `DriverDossier`
   * builds its CAREER scope from `career` + `history` + `liveSeason`, and this
   * screen was passing the last of the three and neither of the first two — so a
   * man with eight wins and a runner-up championship behind him opened on a
   * grid and read "1 season, 1 start". Nothing threw, nothing was wrong in the
   * fold, and the card was simply asked a question with two thirds of the
   * evidence missing.
   *
   * Optional because this panel also runs a league in its FIRST season, where
   * there is genuinely nothing behind it.
   */
  career?: CareerSeason | null;
  history?: readonly CareerSeason[];
  /** Race 36 is in the books: settle the money and the winter. */
  onSettle: () => void;
  /** True while `onSettle` is working — a full winter is a second of arithmetic. */
  settling?: boolean;
  /** Put the season down. The shell asks for the confirmation, not this screen. */
  onLeave: () => void;
  /**
   * D-EZ-103 — THE OTHER WAY THROUGH A SEASON, on the same screen. The Career
   * tab used to own "simulate"; there is now one season home, and it offers
   * both: play the next race, or let the rest of the year run instantly. The
   * shell runs the SAME `runSeasonRace`, so a simulated race is the race that
   * would have been played.
   */
  onSimulate?: (target: 'playoffs' | 'end') => void;
  /** How far a simulation has got, while one is running. */
  simulating?: { done: number; total: number; label: string } | null;
}

export default function SeasonPlay({
  state,
  leagueName,
  seasonNumber,
  teams,
  onGreen,
  onHandRoll,
  handRolledStep = null,
  career = null,
  history = [],
  onSettle,
  settling = false,
  onLeave,
  onSimulate,
  simulating = null,
}: SeasonPlayProps) {
  const [stage, setStage] = useState<Stage>('ready');
  /** The race that has been run and not yet taken the green. */
  const [step, setStep] = useState<SeasonRaceStep | null>(null);
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [handRolled, setHandRolled] = useState(false);
  const [dossier, setDossier] = useState<string | null>(null);
  const [error, setError] = useState<FriendlyError | null>(null);
  /**
   * Putting a season down takes it off the workbench, and there is no undo.
   *
   * ⚠ WHAT THIS COMMENT USED TO SAY, AND WHY IT WAS WRONG. It said "nothing is
   * autosaved", which is true, and drew from it that a season put down is a
   * season gone, which is no longer true: a season in progress can be saved to
   * the account slot at any race (`lib/leagueSlot.saveSeasonSlot`) and loaded back
   * at the exact next race. So the two-step confirmation STAYS — it is still
   * destructive for a season nobody has saved, which is most of them, and making
   * it one click would make losing twenty races easier than it is now — but it
   * names the cost of the CLICK rather than claiming a permanence the game does
   * not have.
   */
  const [leaving, setLeaving] = useState(false);
  /** D-EZ-103 — "simulate the rest" spends races the player might have raced: one confirmation. */
  const [simArmed, setSimArmed] = useState(false);

  const progress = useMemo(() => seasonProgress(state), [state]);
  /** D-EZ-109 — the live season as the Wire and the paper read it. */
  const liveForWire = useMemo(() => ({ seasonNumber, roster: state.roster, teams: [...teams], state }), [seasonNumber, state, teams]);
  const paper = usePaperReader({ history, career, live: liveForWire, leagueName });
  const scheduled = useMemo(() => nextScheduledRace(state), [state]);
  const complete = isSeasonComplete(state);

  /**
   * A NEW RACE NUMBER IS A NEW SCREEN. The step below belongs to one race; when
   * the shell hands back a state that has moved on, the reveal on screen is
   * about a race that is already in the books.
   */
  useEffect(() => {
    setStep(null);
    setStage('ready');
    setCursor(0);
    setPlaying(false);
    setHandRolled(false);
    setError(null);
  }, [state.nextRace, state.seasonId]);

  const cards = useMemo(
    () => new Map<string, DriverCard>(state.roster.map((c) => [c.driverId, c])),
    [state.roster],
  );

  const teamNames = useMemo(
    () => new Map<string, string>(teams.map((t) => [t.teamId, t.name])),
    [teams],
  );

  const liveries = useMemo(() => {
    const seats = new Map<string, { team: Team; index: number }>();
    for (const team of teams) team.driverIds.forEach((id, index) => seats.set(id, { team, index }));
    const map = new Map<string, Livery>();
    for (const card of state.roster) {
      const seat = seats.get(card.driverId);
      map.set(card.driverId, liveryForDriver(card.driverId, card.teamId, seat?.index, seat?.team.baseInk, card.make));
    }
    return map;
  }, [state.roster, teams]);

  /**
   * RUN RACE N. One engine call, and everything on the next two screens is read
   * off what it returned.
   */
  const runRaceWeek = useCallback(() => {
    try {
      const next = runSeasonRace(state);
      setStep(next);
      setCursor(0);
      setPlaying(true);
      setError(null);
      setStage('week');
    } catch (e) {
      setError(friendlyError(e, 'running race week'));
    }
  }, [state]);

  /**
   * A HAND-ROLLED WEEKEND ARRIVES AND IS TOLD LIKE ANY OTHER.
   *
   * The dice table finishes above this screen and hands the finished
   * `SeasonRaceStep` down. From here on nothing is special about it: the same
   * `story`, the same grid, the same green flag. One presentation path for both
   * kinds of weekend is what stops the hand-rolled one quietly drifting into a
   * second, worse version of the screen.
   */
  useEffect(() => {
    if (!handRolledStep) return;
    if (step === handRolledStep) return;
    setStep(handRolledStep);
    setCursor(0);
    setPlaying(true);
    setError(null);
    setStage('week');
  }, [handRolledStep, step]);

  /**
   * D-EZ-56 — WHAT EACH FEUDING PAIR HAS ALREADY DONE TO EACH OTHER.
   *
   * Folded out of the season's own stream plus the week in hand, which is not
   * in that stream yet: the week is generated here and only committed when the
   * player takes the green. Without `week` the card would show a fresh feud
   * with no visible cause, which is the one thing the chevron exists to fix.
   */
  const feudHistory = useMemo(() => {
    if (!step) return undefined;
    const raceNames = new Map<number, string>(
      state.races.map((r) => [r.raceNumber, r.scheduled.raceName]),
    );
    return feudHistories(step.week.feuds, {
      season: state.events,
      week: step.week.events,
      weekRaceNumber: state.nextRace,
      weekRaceName: scheduled?.raceName ?? null,
      raceNames,
    });
  }, [step, state.races, state.events, state.nextRace, scheduled]);

  const story = useMemo(
    () => (step ? weekStory(step.week, state.roster.length) : null),
    [step, state.roster.length],
  );
  const beatCount = story?.beats.length ?? 0;
  const revealed = Math.min(cursor, beatCount);
  const weekTold = revealed >= beatCount;

  // The tape. One beat, then the next, until the week has been told.
  useEffect(() => {
    if (!story || !playing) return;
    if (cursor >= story.beats.length) {
      setPlaying(false);
      return;
    }
    const timer = window.setTimeout(() => setCursor((c) => c + 1), BEAT_MS);
    return () => window.clearTimeout(timer);
  }, [story, playing, cursor]);

  const toTheGrid = useCallback(() => {
    setPlaying(false);
    setCursor(beatCount);
    setStage('grid');
  }, [beatCount]);

  // ---- the season is over -------------------------------------------------
  if (complete) {
    return (
      <section className="seasonplay" aria-label="Season complete">
        <header className="seasonplay__head">
          <p className="kicker">{leagueName} · season {seasonNumber}</p>
          <h2>All {SEASON_RACES} races are run</h2>
          <p className="seasonplay__line">{championLine(state)}</p>
        </header>
        <div className="seasonplay__row">
          <button type="button" className="btn btn--hot btn--big" onClick={onSettle} disabled={settling}>
            {settling ? 'Settling…' : 'Settle the books and the winter'}
          </button>
          <LeaveButton
            racesRun={state.races.length}
            leaving={leaving}
            setLeaving={setLeaving}
            onLeave={onLeave}
            disabled={settling}
          />
        </div>
      </section>
    );
  }

  if (!scheduled) {
    // Cannot happen — `complete` is the only way `nextScheduledRace` is null —
    // and it is stated rather than cast away.
    return <p className="err">The calendar has no race {state.nextRace}.</p>;
  }

  return (
    <div className="seasonplay">
      <header className="seasonplay__head">
        <p className="kicker">
          {leagueName} · season {seasonNumber}
          <Cite> · seed {state.seed}</Cite>
        </p>
        <h2 className="seasonplay__racename">
          {/* The crest, if this race has one. Space is reserved, so a mark
              arriving late does not shove the name sideways. */}
          <RaceLogo raceKey={scheduled.raceKey} size={44} />
          {scheduled.raceName}
        </h2>
        <p className="seasonplay__line">
          Race {scheduled.raceNumber} of {SEASON_RACES} · {scheduled.trackName} · {scheduled.turns} turns
          {scheduled.doublePoints ? ' · double points' : ''}
        </p>
      </header>

      <ErrorNote error={error} />

      {/* ==================================================== 1 · READY ==== */}
      {stage === 'ready' && (
        <>
          {/* D-EZ-110 — the season's opening page, or the chapter that just closed. */}
          <SeasonChapterCard state={state} seasonNumber={seasonNumber} teams={teams} history={history} career={career} />
          {/*
            THE DICE CHOICE IS MADE HERE, BEFORE A DIE IS THROWN.

            It used to sit at the grid, which was honest while only the RACE was
            hand-rolled — the week was identical either way. Now the week is
            hand-rolled too, so choosing at the grid would mean showing a player
            his TV list, his feuds and his starting grid and then throwing all
            three away. The choice belongs before the first roll, and the label
            says what it covers.
          */}
          <label className="rwhandoff__dice">
            <input
              type="checkbox"
              checked={handRolled}
              onChange={(e) => setHandRolled(e.target.checked)}
            />{' '}
            <strong>Roll my own dice.</strong>{' '}
            <span className="dim">
              The whole weekend — qualifying, the TV list and the feuds (about 35 throws), then the
              race (about 900). You can hand the rest back to the engine at any point.
            </span>
          </label>

          <div className="seasonplay__row">
            <button
              type="button"
              className="btn btn--hot btn--big seasonplay__go"
              onClick={handRolled ? onHandRoll : runRaceWeek}
              disabled={Boolean(simulating)}
            >
              {handRolled
                ? `Play the next race — race ${scheduled.raceNumber}, your dice`
                : `Play the next race — race ${scheduled.raceNumber}`}
            </button>
            {onSimulate && (
              <button
                type="button"
                className="btn seasonplay__go"
                onClick={() => setSimArmed(true)}
                disabled={Boolean(simulating)}
                aria-expanded={simArmed}
              >
                Simulate the rest of the season
              </button>
            )}
            {onSimulate && state.nextRace <= REGULAR_SEASON_RACES && (
              <button
                type="button"
                className="btn btn--quiet seasonplay__go"
                onClick={() => onSimulate('playoffs')}
                disabled={Boolean(simulating)}
              >
                Simulate to the playoffs
              </button>
            )}
          </div>

          {simulating && (
            <div className="ff__running" role="status" aria-live="polite">
              <span>
                Racing {simulating.label} — {simulating.done} of {simulating.total}
              </span>
              <div className="career__bar-track">
                <div
                  className="career__bar-fill"
                  style={{ width: `${(simulating.done / Math.max(1, simulating.total)) * 100}%` }}
                />
              </div>
            </div>
          )}

          {simArmed && !simulating && onSimulate && (
            <div className="ff__sure" role="alertdialog" aria-label="Simulate the rest of the season">
              <p>
                This races the remaining <b>{progress.racesRemaining} weekends</b> instantly — nothing to watch —
                then settles the year and runs the winter. Every result still goes in the book.
              </p>
              <div className="ff__acts">
                <button type="button" className="btn btn--hot seasonplay__go" onClick={() => setSimArmed(false)} autoFocus>
                  Not yet
                </button>
                <button
                  type="button"
                  className="btn btn--quiet seasonplay__go"
                  onClick={() => {
                    setSimArmed(false);
                    onSimulate('end');
                  }}
                >
                  Race them all and settle the year
                </button>
              </div>
            </div>
          )}

          {/*
            D-EZ-109 — THE WIRE. The week's news, written by the game (no
            model call), between the race just run and the one above. Back
            weeks are one tap away; the race write-up opens the paper.
          */}
          {state.races.length > 0 && !simulating && (
            <SeasonWire live={liveForWire} history={history} career={career} onReadRace={(s, n) => paper.open('race', s, n)} />
          )}

          <Standings state={state} />

          <ol className="schedlist" aria-label="What is left of the calendar">
            {state.schedule.races
              .slice(scheduled.raceNumber - 1, scheduled.raceNumber + 4)
              .map((race) => (
                <li key={race.raceKey} className={race.raceNumber === scheduled.raceNumber ? 'is-next' : undefined}>
                  <span className="schedlist__n">{race.raceNumber}</span>
                  <span className="schedlist__r">
                    <RaceLogo raceKey={race.raceKey} size={22} />
                    {race.raceName}
                  </span>
                  <span className="schedlist__t">
                    {race.trackName} · {race.turns}t{race.doublePoints ? ' · 2×' : ''}
                  </span>
                </li>
              ))}
          </ol>
          <p className="field__note">{progress.racesRemaining} races left on this calendar.</p>
          <div className="seasonplay__row seasonplay__row--quiet">
            <LeaveButton
              racesRun={state.races.length}
              leaving={leaving}
              setLeaving={setLeaving}
              onLeave={onLeave}
            />
          </div>
        </>
      )}

      {paper.modal}

      {/* ===================================================== 2 · WEEK ==== */}
      {stage === 'week' && story && (
        <div className="rw__week">
          <div className="rwbar">
            <div className="rwbar__controls">
              <button
                type="button"
                className="btn btn--quiet"
                onClick={() => setPlaying((p) => !p)}
                disabled={weekTold}
              >
                {playing ? 'Hold' : 'Play'}
              </button>
              <button
                type="button"
                className="btn btn--quiet"
                onClick={() => {
                  setPlaying(false);
                  setCursor((c) => Math.min(beatCount, c + 1));
                }}
                disabled={weekTold}
              >
                Next beat
              </button>
            </div>
            <p className="rwbar__count" role="status" aria-live="polite">
              {weekTold ? `${story.starters} cars in the field.` : `${revealed} of ${beatCount} beats`}
            </p>
            <button type="button" className="btn btn--hot" onClick={toTheGrid}>
              {weekTold ? 'See the starting grid' : 'Skip to the grid'}
            </button>
          </div>

          <WeekReveal
            story={story}
            cards={cards}
            teamNames={teamNames}
            cursor={revealed}
            feudHistory={feudHistory}
            raceNumber={state.nextRace}
          />
        </div>
      )}

      {/* ===================================================== 3 · GRID ==== */}
      {stage === 'grid' && story && step && (
        <div className="rw__grid">
          <StartingGrid
            rows={story.grid}
            dnq={story.dnq}
            bubble={story.bubbleUnused}
            cards={cards}
            liveries={liveries}
            teamNames={teamNames}
            rosterSize={story.rosterSize}
            onSelectDriver={setDossier}
          />

          {dossier && (
            <DriverDossier
              driverId={dossier}
              week={{
                raceName: scheduled.raceName,
                row: story.grid.find((r) => r.card.driverId === dossier),
                standing: story.grid.some((r) => r.card.driverId === dossier)
                  ? 'grid'
                  : story.dnq.includes(dossier)
                    ? 'dnq'
                    : 'bubble',
                cards,
                // Grid order, then the ones who did not make the show — so ‹ ›
                // walks the field in the order this screen lists it.
                order: [...story.grid.map((r) => r.card.driverId), ...story.dnq],
                teams: [...teams],
              }}
              /*
                D-EZ-60 — the season and the career behind the grid. The card is
                opened from a starting grid, but the man standing on it is in the
                middle of a championship and the modal can now say so.
              */
              season={{
                seasonNumber,
                cards,
                teams: [...teams],
                standings: state.standingsAfterRace.at(-1),
                throughRace: state.nextRace - 1,
                totalRaces: SEASON_RACES,
              }}
              liveSeason={{ seasonNumber, roster: state.roster, teams: [...teams], state }}
              /* D-EZ-67 — see `SeasonPlayProps.career`. Without these two the
                 CAREER scope on this screen could only ever see this year. */
              career={career ?? undefined}
              history={[...history]}
              onClose={() => setDossier(null)}
              onSelectDriver={setDossier}
            />
          )}

          {/* D-EZ-102 — "Storylines to watch", the TV panel before the green. */}
          <RaceWeekStorylines
            state={state}
            seasonNumber={seasonNumber}
            teams={teams}
            history={history}
            career={career}
            week={step.week}
          />

          <div className="rwhandoff rwhandoff--green">
            <p className="kicker">
              Race {scheduled.raceNumber} of {SEASON_RACES}
            </p>
            <h2>Take the green</h2>
            <p className="rwhandoff__line">
              {Object.keys(step.week.tvList).length} on the TV list · {step.week.feuds.length} feud
              {step.week.feuds.length === 1 ? '' : 's'} · {step.week.fuming.length} fuming ·{' '}
              {Object.values(step.week.chips).reduce((n, c) => n + c, 0)} chips in hand
              {step.result.absences.length > 0
                ? ` · ${step.result.absences.length} not in the field`
                : ''}
              .
            </p>
            {/*
              NO DICE CHOICE HERE ANY MORE — it is made at 'ready', because by
              this point the weekend has been decided and this grid is the real
              one. Offering the dice here would offer to replace what is on
              screen. If this weekend WAS hand-rolled, the grid above came off
              the player's own dice and the green is the only thing left.
            */}
            {handRolled && (
              <p className="dim small">
                This grid came off your dice. The race ahead is on your dice too.
              </p>
            )}
            <div className="rwhandoff__row">
              <button
                type="button"
                className="btn btn--green btn--big"
                onClick={() => onGreen(step)}
              >
                Drop the green
              </button>
              <button type="button" className="btn btn--quiet" onClick={() => setStage('week')}>
                Back to the week
              </button>
              <button
                type="button"
                className="btn btn--quiet"
                onClick={() => {
                  setStep(null);
                  setStage('ready');
                }}
              >
                Not yet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * ONE CLICK TO ASK, ONE TO DO IT, and the second label says how much.
 *
 * Nothing here is saved automatically — by design, Howard's — so a season that
 * the player has not put in the slot really does end here. The count is on the
 * button because "discard" without a number is a word, and "discard 20 races" is
 * a decision.
 */
function LeaveButton({
  racesRun,
  leaving,
  setLeaving,
  onLeave,
  disabled = false,
}: {
  racesRun: number;
  leaving: boolean;
  setLeaving: (v: boolean) => void;
  onLeave: () => void;
  disabled?: boolean;
}) {
  if (!leaving) {
    return (
      <button type="button" className="btn btn--quiet" onClick={() => setLeaving(true)} disabled={disabled}>
        Put the season down
      </button>
    );
  }
  return (
    <>
      <button type="button" className="btn btn--danger" onClick={onLeave} disabled={disabled}>
        Discard {racesRun} race{racesRun === 1 ? '' : 's'} — no undo
      </button>
      <button type="button" className="btn btn--quiet" onClick={() => setLeaving(false)}>
        Keep it
      </button>
    </>
  );
}

/**
 * THE TOP OF THE POINTS, as they stand — read off the last race's standings and
 * never accumulated here. Empty before race 1, which is a fact and not a gap.
 */
function Standings({ state }: { state: SeasonState }) {
  const standings = state.standingsAfterRace[state.standingsAfterRace.length - 1] ?? null;
  const last = state.races[state.races.length - 1] ?? null;
  if (!standings || !last) {
    return <p className="field__note">Nobody has scored a point yet.</p>;
  }
  const top = standings.drivers.slice(0, 5);
  const winner = last.race.winnerId
    ? (standings.byDriver[last.race.winnerId]?.name ?? last.race.winnerId)
    : null;
  return (
    <div className="seasonplay__standings">
      <p className="field__note">
        Race {last.raceNumber}: {winner ? `${winner} won at ${last.scheduled.trackName}` : 'no winner recorded'}.
      </p>
      <ol className="seasonplay__points">
        {top.map((d) => (
          <li key={d.driverId}>
            <span className="schedlist__n">{ordinal(d.rank)}</span>
            <span className="schedlist__r">{d.name}</span>
            <span className="schedlist__t">
              {d.points} pts · {d.wins} win{d.wins === 1 ? '' : 's'}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * One line about how the year ended.
 *
 * The CHAMPION is D-012's playoff winner, not the points leader, and it is read
 * out of exactly the field `finishSeason` reads it out of — the championship
 * round's lone survivor. Naming the points leader here instead would be a
 * plausible wrong answer, which is the one kind this project will not print.
 */
function championLine(state: SeasonState): string {
  const standings = state.standingsAfterRace[state.standingsAfterRace.length - 1];
  const championId = state.playoff.rounds.find((r) => r.round === 'championship')?.advanced[0] ?? null;
  if (championId) {
    const name = standings?.byDriver[championId]?.name ?? championId;
    return `${name} takes the Cup.`;
  }
  const leader = standings?.drivers[0];
  return leader
    ? `No champion came out of the bracket. ${leader.name} led on points with ${leader.points}.`
    : 'The season produced no standings.';
}
